// ✅ ALTERED ONLY FOR THEME (matches your “above theme”)
// - Orange header bar + white text
// - Page bg #F2F2F2 + inner white surfaces with soft blue border
// - Left orange accent stripe for Filters section
// - Inputs/autocomplete: soft blue border + orange focus
// - Buttons: primary orange, outlined blue
// - Tables: blue header, soft zebra rows, hover tint, subtle borders
// - IconButtons: blue default, orange on hover
// - No business/API logic changed

import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  Autocomplete,
  TextField,
  Checkbox,
  Button,
  IconButton,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { API_URL, API_UPLOAD_URL } from "./config";

const StudentPromotionReport = () => {
  // ✅ Theme constants
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
    "&.Mui-disabled": { opacity: 0.7, color: "#fff" },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const iconBtnSx = {
    borderRadius: 2,
    color: "rgba(25,118,210,0.95)",
    "&:hover": { backgroundColor: "rgba(240,120,40,0.10)", color: THEME_ORANGE },
  };

  const tableWrapSx = {
    ...surfaceSx,
    mt: 2,
    overflow: "hidden",
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
  });

  // ===== Original logic =====
  const [originalData, setOriginalData] = useState([]);
  const [data, setData] = useState([]);
  const [campusCode, setCampusCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // filters
  const [fSubCamp, setFSubCamp] = useState([]);
  const [fDegree, setFDegree] = useState([]);
  const [fBranch, setFBranch] = useState([]);
  const [fSemester, setFSemester] = useState([]);

  // edit state
  const [editIdx, setEditIdx] = useState(null);

  // selected rows
  const [selected, setSelected] = useState(new Set());

  const token = localStorage.getItem("auth");

  // decode campusCode
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { campusCode: cc } = JSON.parse(atob(token.split(".")[1]));
      setCampusCode(cc);
    } catch {
      setError("Invalid auth token");
    } finally {
      setLoading(false);
    }
  }, []); // keep as-is

  // fetch data
  useEffect(() => {
    if (!campusCode) return;
    setLoading(true);
    axios
      .get(`${API_URL}/api/StudentPromotion`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setOriginalData(res.data);
        setData(res.data);
      })
      .catch(() => setError("Failed to fetch data"))
      .finally(() => setLoading(false));
  }, [campusCode]);

  // derive unique options for filters
  const options = useMemo(() => {
    const subs = new Set(),
      degs = new Set(),
      brs = new Set(),
      sems = new Set();
    originalData.forEach((r) => {
      subs.add(r.SubCampusCode);
      degs.add(r.DegreeCode);
      brs.add(r.BranchCode);
      sems.add(r.Semester);
    });
    return {
      subs: Array.from(subs).filter(Boolean),
      degs: Array.from(degs).filter(Boolean),
      brs: Array.from(brs).filter(Boolean),
      sems: Array.from(sems).filter((x) => x !== undefined && x !== null),
    };
  }, [originalData]);

  // apply front-end filters
  useEffect(() => {
    let filtered = originalData;
    if (fSubCamp.length) filtered = filtered.filter((r) => fSubCamp.includes(r.SubCampusCode));
    if (fDegree.length) filtered = filtered.filter((r) => fDegree.includes(r.DegreeCode));
    if (fBranch.length) filtered = filtered.filter((r) => fBranch.includes(r.BranchCode));
    if (fSemester.length) filtered = filtered.filter((r) => fSemester.includes(r.Semester));
    setData(filtered);
  }, [fSubCamp, fDegree, fBranch, fSemester, originalData]);

  const handleEdit = (idx) => setEditIdx(idx);

  const handleSaveNewSem = (idx) => {
    // your existing logic was just closing edit; keep same
    setEditIdx(null);
  };

  const handleNewSemChange = (e, idx) => {
    const newVal = e.target.value;
    setData((d) => d.map((r, i) => (i === idx ? { ...r, NewSemester: newVal } : r)));
  };

  const toggleSelect = (idx) => {
    const s = new Set(selected);
    s.has(idx) ? s.delete(idx) : s.add(idx);
    setSelected(s);
  };

  const handlePromote = () => {
    if (!selected.size) {
      setError("No rows selected to promote");
      return;
    }
    const items = Array.from(selected).map((idx) => ({
      SubCampusCode: data[idx].SubCampusCode,
      DegreeCode: data[idx].DegreeCode,
      BranchCode: data[idx].BranchCode,
      Semester: data[idx].Semester,
      NewSemester: data[idx].NewSemester,
    }));

    axios
      .post(
        `${API_URL}/api/StudentPromotion/promote`,
        { items },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then(() => {
        setSuccessMsg("Promotion successful!");
        return axios.get(`${API_URL}/api/StudentPromotion`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      })
      .then((res) => {
        setOriginalData(res.data);
        setSelected(new Set());
      })
      .catch(() => setError("Promotion failed"));
  };

  if (loading) return <CircularProgress />;

  const allSelected = selected.size === data.length && data.length > 0;
  const indeterminate = selected.size > 0 && selected.size < data.length;

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Update Student Semester
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Select groups and set next semester, then promote
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              sx={primaryBtnSx}
              onClick={handlePromote}
              disabled={!selected.size}
              title={!selected.size ? "Select at least one row" : "Promote selected rows"}
            >
              Promote Selected
            </Button>
          </Box>
        </Box>

        <Box sx={bodySx}>
          {/* ✅ Filters section */}
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>Filters</Typography>
              <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" },
                  gap: 1,
                }}
              >
                <Autocomplete
                  multiple
                  options={options.subs}
                  value={fSubCamp}
                  onChange={(_, v) => setFSubCamp(v)}
                  renderInput={(params) => <TextField {...params} label="Sub Campus" size="small" sx={inputSx} />}
                />
                <Autocomplete
                  multiple
                  options={options.degs}
                  value={fDegree}
                  onChange={(_, v) => setFDegree(v)}
                  renderInput={(params) => <TextField {...params} label="Degree" size="small" sx={inputSx} />}
                />
                <Autocomplete
                  multiple
                  options={options.brs}
                  value={fBranch}
                  onChange={(_, v) => setFBranch(v)}
                  renderInput={(params) => <TextField {...params} label="Branch" size="small" sx={inputSx} />}
                />
                <Autocomplete
                  multiple
                  options={options.sems}
                  value={fSemester}
                  onChange={(_, v) => setFSemester(v)}
                  renderInput={(params) => <TextField {...params} label="Semester" size="small" sx={inputSx} />}
                />
              </Box>
            </Box>
          </Paper>

          {/* Messages */}
          {error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                mt: 2,
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
              }}
            >
              {error}
            </Alert>
          )}

          {successMsg && (
            <Snackbar open autoHideDuration={3000} onClose={() => setSuccessMsg("")}>
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                {successMsg}
              </Alert>
            </Snackbar>
          )}

          {/* ✅ Table */}
          <Paper elevation={0} sx={tableWrapSx}>
              {/* INNER – ONLY for horizontal scroll */}
              <Box
                sx={{
                  overflowX: "auto",
                  overflowY: "hidden",
                }}
              >
            <TableContainer component="div">
            <Table size="small" sx={{ width: "100%" }}>
              <colgroup>
                <col style={{ width: "3%" }} />
                <col style={{ width: "3%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "17%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "5%" }} />
                <col style={{ width: "12%" }} />
              </colgroup>

              <TableHead>
                <TableRow sx={theadRowSx}>
                  <TableCell>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={indeterminate}
                      onChange={(e) => {
                        if (e.target.checked) setSelected(new Set(data.map((_, i) => i)));
                        else setSelected(new Set());
                      }}
                      sx={{ color: "rgba(255,255,255,0.9)", "&.Mui-checked": { color: "#fff" } }}
                    />
                  </TableCell>
                  {["#", "Campus", "Degree", "Branch", "Current Sem.", "Next Sem.", "Count", "Actions"].map((h) => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {data.map((row, idx) => (
                  <TableRow key={`${row.SubCampusCode}-${idx}`} sx={zebraRowSx(idx)}>
                    <TableCell>
                      <Checkbox
                        checked={selected.has(idx)}
                        onChange={() => toggleSelect(idx)}
                        sx={{ color: "rgba(25,118,210,0.75)", "&.Mui-checked": { color: THEME_ORANGE } }}
                      />
                    </TableCell>

                    <TableCell>{idx + 1}</TableCell>
                    {/* NOTE: your original table cell showed SubCampusCode under "Campus" column; keeping same behavior */}
                    <TableCell>{row.SubCampusCode}</TableCell>
                    <TableCell>{row.DegreeCode}</TableCell>
                    <TableCell>{row.BranchCode}</TableCell>
                    <TableCell>{row.Semester}</TableCell>

                    <TableCell>
                      {editIdx === idx ? (
                        <TextField
                          size="small"
                          value={row.NewSemester || ""}
                          onChange={(e) => handleNewSemChange(e, idx)}
                          placeholder="New Sem."
                          sx={inputSx}
                        />
                      ) : (
                        row.NewSemester || "—"
                      )}
                    </TableCell>

                    <TableCell>{row.Count}</TableCell>

                    <TableCell>
                      {editIdx === idx ? (
                        <>
                          <IconButton sx={iconBtnSx} onClick={() => setEditIdx(null)} title="Cancel">
                            <CancelIcon />
                          </IconButton>
                          <IconButton sx={iconBtnSx} onClick={() => handleSaveNewSem(idx)} title="Save">
                            <SaveIcon />
                          </IconButton>
                        </>
                      ) : (
                        <IconButton sx={iconBtnSx} onClick={() => handleEdit(idx)} title="Edit">
                          <EditIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}

                {data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4, color: "rgba(0,0,0,0.65)" }}>
                      No active students found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
};

export default StudentPromotionReport;
